import { useEffect, useState } from "react";
import type { AssistantProfile, UserProfile } from "@ai-sales/contracts";
import type { SessionChannel } from "./api.js";

const maxAvatarBytes = 512 * 1024;
const supportedAvatarTypes = new Set(["image/png", "image/jpeg", "image/webp"]);

function ProfileAvatar({ profile }: { profile: UserProfile }) {
  if (profile.avatarUrl) return <img src={profile.avatarUrl} alt="" />;
  const initials = profile.nickname.trim().slice(0, 2).toUpperCase() || "AI";
  return <span aria-hidden="true">{initials}</span>;
}

export function AssistantUserSettings(props: {
  profile: UserProfile;
  assistantProfile: AssistantProfile;
  channel: SessionChannel;
  salesforceAvatarUrl?: string;
  busy: boolean;
  onSave: (
    input: Pick<UserProfile, "nickname" | "description" | "avatarSource"> & { avatarUrl?: string }
  ) => Promise<void>;
  onLogout: () => Promise<void>;
  onLogoutAll: () => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(props.profile);
  const [localError, setLocalError] = useState("");

  useEffect(() => setDraft(props.profile), [props.profile]);

  const chooseAvatar = (file?: File) => {
    setLocalError("");
    if (!file) return;
    if (!supportedAvatarTypes.has(file.type)) {
      setLocalError("头像仅支持 PNG、JPEG 或 WebP");
      return;
    }
    if (file.size > maxAvatarBytes) {
      setLocalError("头像不能超过 512KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((current) => ({
      ...current,
      avatarSource: "custom",
      avatarUrl: typeof reader.result === "string" ? reader.result : current.avatarUrl
    }));
    reader.onerror = () => setLocalError("头像读取失败，请重新选择");
    reader.readAsDataURL(file);
  };

  return <aside className="assistant-settings-panel" aria-label="AI 助手设置">
    <header>
      <div>
        <strong>我的账号</strong>
        <span>{props.assistantProfile.name} · 个人资料与登录安全</span>
      </div>
      <button type="button" aria-label="关闭设置" onClick={props.onClose}>×</button>
    </header>
    <section className="assistant-settings-profile">
      <div className="assistant-settings-profile__avatar"><ProfileAvatar profile={draft} /></div>
      <label>昵称<input maxLength={80} value={draft.nickname} onChange={(event) => setDraft((current) => ({ ...current, nickname: event.target.value }))} /></label>
      <label>个人描述<textarea rows={3} maxLength={240} value={draft.description} onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))} /></label>
      <label className="profile-upload">
        上传头像
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => chooseAvatar(event.target.files?.[0])} />
        <small>PNG、JPEG 或 WebP，不超过 512KB</small>
      </label>
      <div className="avatar-source-actions" role="group" aria-label="头像来源">
        {props.salesforceAvatarUrl ? <button type="button" className={draft.avatarSource === "salesforce" ? "active" : ""} onClick={() => setDraft((current) => ({
          ...current,
          avatarSource: "salesforce",
          avatarUrl: props.salesforceAvatarUrl
        }))}>Salesforce 头像</button> : null}
        <button type="button" className={draft.avatarSource === "initials" ? "active" : ""} onClick={() => setDraft((current) => ({
          ...current,
          avatarSource: "initials",
          avatarUrl: undefined
        }))}>昵称缩写</button>
      </div>
      {localError ? <div className="error-banner" role="alert">{localError}</div> : null}
      <button
        className="button button--primary"
        type="button"
        disabled={props.busy || !draft.nickname.trim()}
        onClick={() => void props.onSave({
          nickname: draft.nickname.trim(),
          description: draft.description.trim(),
          avatarSource: draft.avatarSource,
          ...(draft.avatarUrl ? { avatarUrl: draft.avatarUrl } : {})
        })}
      >{props.busy ? "保存中…" : "保存个人资料"}</button>
    </section>
    <section className="assistant-settings-security">
      <h3>登录与安全</h3>
      <p>{props.channel === "salesforce"
        ? "当前由 Salesforce 身份自动登录。退出 AI 助手不会退出 Salesforce。"
        : "当前登录仅作用于这个 AI 助手入口。"}</p>
      <button type="button" disabled={props.busy} onClick={() => void props.onLogout()}>
        {props.channel === "salesforce" ? "退出 AI 助手" : "退出当前登录"}
      </button>
      <button type="button" disabled={props.busy} onClick={() => void props.onLogoutAll()}>退出所有设备</button>
    </section>
  </aside>;
}
